import { createPortal } from 'react-dom';
import { Card, Conditional } from '@consumidor-positivo/aurora';
import { Title, Highlights, Tag, Buttons } from './components';
import { RedirectModal } from '../RedirectModal';

import { useOfferRedirect } from '../../hooks/useOfferRedirect';
import { useOfferCard } from './hooks';
import { getSafeUser } from '../../helpers/getSafeUser';

import { SHOffer } from '../../services/sortingHat/types';
import { ExtraFunctionsType } from '../../types';
import './styles.scss';

type OfferCardProps = {
  offer: SHOffer;
  index: number;
  location: string;
  user: any;
  smallCard?: boolean;
  onClickOffer?: ExtraFunctionsType;
  onViewOffer?: (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => void;
};

export const OfferCard = ({ offer, index, location, user, smallCard, onClickOffer, onViewOffer }: OfferCardProps) => {
  const safeUser = user && getSafeUser(user);
  const { isLoading, handleOfferRedirect, handleDetailRedirect, isRedirectModalOpen } = useOfferRedirect({
    location,
    user: safeUser,
    extraFunctions: onClickOffer,
  });
  const { cardRef } = useOfferCard({
    offer,
    index,
    location,
    user: safeUser,
    onViewOffer,
  });

  const {
    generalInfo: { limit, limitMessage, preApproved, annuity },
    content: {
      image_logo,
      title,
      title_highlight,
      highlight_card,
      button_text,
      description,
      detail_page,
      partner_friendly_name,
      feature_information,
      callout_tag,
    },
  } = offer;

  const showDetailButton = Boolean(detail_page) && Boolean(partner_friendly_name);
  const partnerName = partner_friendly_name || 'o parceiro';
  const tagType = limit || preApproved ? 'approved' : 'default';
  if (smallCard) {
    return (
      <div ref={cardRef} className="creditkit-offer-card creditkit-offer-card--small">
        <Card.Root border={false} color="secondary">
          <Card.Container gap={16}>
            <Conditional condition={Boolean(callout_tag)} renderIf={<div className="creditkit-offer-card__callout-tag"></div>} />
            <Tag text={feature_information} type={tagType} />
            <Card.Container direction="row" alignItems="center" gap={16}>
              <Card.Image height={64} src={image_logo.filename} alt={image_logo.alt} />
              <Title text={title} description={description} limitMessage={limitMessage} titleHighlight={title_highlight} smallCard />
            </Card.Container>
            <Buttons
              text={button_text}
              showDetailButton={showDetailButton}
              onClickOffer={() => handleOfferRedirect({ offer, index })}
              onClickDetailOffer={() => handleDetailRedirect(offer, index)}
              loading={isLoading}
              smallCard
            />
          </Card.Container>
        </Card.Root>
        <Conditional
          condition={isRedirectModalOpen}
          renderIf={
            <>
              {createPortal(
                <RedirectModal isOpen={isRedirectModalOpen} partnerName={partnerName} offerImage={image_logo?.filename} />,
                document.body
              )}
            </>
          }
        />
      </div>
    );
  }

  return (
    <div ref={cardRef} className="creditkit-offer-card">
      <Card.Root maxWidth={308} height={474}>
        <Card.Container gap={16} alignItems="center">
          <Conditional condition={Boolean(callout_tag)} renderIf={<div className="creditkit-offer-card__callout-tag"></div>} />
          <Tag text={feature_information} type={tagType} />
          <Card.Image height={72} src={image_logo.filename} alt={image_logo.alt} />
          <Title text={title} description={description} limitMessage={limitMessage} titleHighlight={title_highlight} />
          <Highlights highlights={highlight_card} annuity={annuity} />
          <Buttons
            text={button_text}
            showDetailButton={showDetailButton}
            onClickOffer={() => handleOfferRedirect({ offer, index })}
            onClickDetailOffer={() => handleDetailRedirect(offer, index)}
            loading={isLoading}
          />
        </Card.Container>
      </Card.Root>

      <Conditional
        condition={isRedirectModalOpen}
        renderIf={
          <>
            {createPortal(<RedirectModal isOpen={isRedirectModalOpen} partnerName={partnerName} offerImage={image_logo?.filename} />, document.body)}
          </>
        }
      />
    </div>
  );
};
